/* eslint-disable menti-react/filename-convention--jsx */
import * as React from 'react';
import { SidebarIcon } from '@mentimeter/ragnar-visuals';
import { designSystemConfig } from '@mentimeter/ragnar-dsc';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { ThemeBgSensitive } from '@mentimeter/ragnar-ui/themes';
import {
  TooltipBase,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@mentimeter/ragnar-ui/tooltip';
import {
  ToolbarModalsProvider,
  useHotkeyOverlay,
  usePace,
  useQuestion,
  useTheme,
} from '../data-mediation';
import { Exit } from './presentation-toolbar/Exit';
import { Mentimote } from './presentation-toolbar/Mentimote';
import { ResetResultsModal } from './presentation-toolbar/ResetResultsModal';
import { DeleteSessionModal } from './slide-toolbar/DeleteSessionModal';
import { ControlsWrapper } from './ControlsWrapper';
import { ParticipationTooltip } from './ParticipationTooltip';
import {
  ToolbarVisibilityProvider,
  useToolbarVisiblityState,
} from './ToolbarVisiblityProvider';

export const ExitControl = () => {
  const { isShowing } = useToolbarVisiblityState();
  const showHotkeyOverlay = useHotkeyOverlay((state) => state.show);
  if (showHotkeyOverlay) return null;
  return (
    <ControlsWrapper
      show={isShowing}
      extend={({ theme }) => ({
        left: `${theme.kosmosSpacing.space6}px`,
        top: `${theme.kosmosSpacing.space6}px`,
      })}
    >
      <Exit />
    </ControlsWrapper>
  );
};

export const MentimoteControl = () => {
  const { isShowing } = useToolbarVisiblityState();
  const showHotkeyOverlay = useHotkeyOverlay((state) => state.show);
  const showEndStatus = usePace().state?.hasReachedEnd;
  if (showHotkeyOverlay || showEndStatus) return null;

  return (
    <ControlsWrapper
      show={isShowing}
      position="relative"
      extend={({ theme }) => ({
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: theme.colors.surfaceOverlayTransparentHover,
          },
        },
      })}
    >
      <Mentimote />
    </ControlsWrapper>
  );
};

export const ParticipationViewControl = ({
  onClick,
  participationViewVisible,
}: {
  onClick: () => void;
  participationViewVisible: boolean;
}) => {
  const participationTooltipId = React.useId();
  const { isShowing } = useToolbarVisiblityState();
  const showHotkeyOverlay = useHotkeyOverlay((state) => state.show);
  const showEndStatus = usePace().state?.hasReachedEnd;
  if (showHotkeyOverlay || showEndStatus) return null;

  return (
    <ControlsWrapper
      show={isShowing}
      position="relative"
      extend={({ theme }) => ({
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: theme.colors.surfaceOverlayTransparentHover,
          },
        },
      })}
    >
      <TooltipBase>
        <TooltipTrigger asChild>
          <Clickable
            id={participationTooltipId}
            aria-label="Open participation view"
            alignItems="center"
            justifyContent="space-evenly"
            flexDirection="row"
            onClick={onClick}
            borderRadius="full"
            px={3}
            py={3}
            color="primary"
            gap="space4"
            extend={() => ({
              opacity: 1,
              '@media (hover: hover)': {
                ':hover': {
                  opacity: 1,
                },
              },
            })}
          >
            <SidebarIcon color="inherit" />
          </Clickable>
        </TooltipTrigger>
        <TooltipPortal forceMount>
          <TooltipContent
            forceMount
            side="bottom"
            sideOffset={designSystemConfig.kosmosSpacing.space2}
            collisionPadding={designSystemConfig.kosmosSpacing.space2}
          >
            {!participationViewVisible && (
              <ParticipationTooltip referenceId={participationTooltipId} />
            )}
          </TooltipContent>
        </TooltipPortal>
      </TooltipBase>
    </ControlsWrapper>
  );
};

export const Toolbar = ({
  children,
  alwaysShow = false,
}: {
  children: React.ReactNode;
  alwaysShow?: boolean;
}) => {
  const { background_color } = useTheme();
  const { theme_settings } = useQuestion();
  const themeBg = theme_settings?.background_color ?? background_color;

  return (
    <ToolbarVisibilityProvider alwaysShow={alwaysShow}>
      <ToolbarModalsProvider>
        <ThemeBgSensitive themeBg={themeBg}>{children}</ThemeBgSensitive>
        <ResetResultsModal />
        <DeleteSessionModal />
      </ToolbarModalsProvider>
    </ToolbarVisibilityProvider>
  );
};
